import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent } from "../components/ui/dialog";
import {
  ArrowLeft, Globe, Loader2, Lock, Mail, Eye, EyeOff,
  CheckCircle2, XCircle, AlertCircle, Search, Sparkles, Shield,
  Zap, Crown, Link2, ChevronDown, ChevronUp, Copy, ExternalLink, Info
} from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debouncedValue;
}

// ── DNS provider step-by-step guides (Nameserver ändern) ─────────────────────
const PROVIDER_GUIDES = {
  IONOS: [
    "Melde dich bei mein.ionos.de an",
    "Gehe zu 'Domains & SSL' > deine Domain",
    "Klicke auf 'Nameserver' > 'Nameserver anpassen'",
    "Waehle 'Andere Nameserver verwenden'",
    "Gib NS1 und NS2 ein und speichere",
  ],
  Strato: [
    "Melde dich bei strato.de an",
    "Domainverwaltung > deine Domain > 'Nameserver'",
    "Waehle 'Fremde Nameserver'",
    "NS1 und NS2 eintragen und speichern",
  ],
  GoDaddy: [
    "Gehe zu godaddy.com > Mein Konto > Domains",
    "Klicke auf deine Domain > 'DNS verwalten'",
    "Nameserver > 'Aendern' > 'Eigene Nameserver'",
    "NS1 und NS2 eintragen und speichern",
  ],
  "All-Inkl": [
    "Melde dich bei all-inkl.com im KAS an",
    "Domains > deine Domain > 'Nameserver'",
    "Externe Nameserver: NS1 und NS2 eintragen",
    "Uebernehmen klicken",
  ],
};

const DEFAULT_GUIDES = [
  "DNS-Verwaltung deines Anbieters oeffnen",
  "Bereich 'Nameserver' suchen",
  "Eigene Nameserver auswaehlen und NS1 + NS2 eintragen",
  "Speichern",
];

function DNSGuide({ provider, ns1, ns2 }) {
  const steps = (PROVIDER_GUIDES[provider] || DEFAULT_GUIDES);
  return (
    <ol className="space-y-2 mt-2">
      {steps.map((step, i) => (
        <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
          <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
          {step === "NS1 und NS2 eintragen und speichern" || step === "NS1 und NS2 eintragen" || step === "Externe Nameserver: NS1 und NS2 eintragen" || step === "Eigene Nameserver auswaehlen und NS1 + NS2 eintragen"
            ? <span>{step.split("NS1")[0]}<code className="bg-indigo-50 px-1 rounded text-indigo-700 text-xs">{ns1}</code> und <code className="bg-indigo-50 px-1 rounded text-indigo-700 text-xs">{ns2}</code>{step.split("NS2").slice(1)}</span>
            : step}
        </li>
      ))}
    </ol>
  );
}

export default function PublishPage() {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [publishingStatus, setPublishingStatus] = useState("");

  // Domain type
  const [domainType, setDomainType] = useState(null); // null | "custom" | "connect"

  // "custom" – buy new domain
  const [customDomain, setCustomDomain] = useState("");
  const [selectedTld, setSelectedTld] = useState(".de");
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);
  const [domainError, setDomainError] = useState("");
  const [domainPrice, setDomainPrice] = useState(null);
  const [domainSource, setDomainSource] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  // "connect" – existing domain
  const [connectDomain, setConnectDomain] = useState("");
  const [connectStep, setConnectStep] = useState("input"); // "input" | "instructions"
  const [detectedProvider, setDetectedProvider] = useState(null);
  const [detectingProvider, setDetectingProvider] = useState(false);
  const [serverInfo, setServerInfo] = useState(null);
  const [nameservers, setNameservers] = useState([]); // Cloudflare NS
  const [cfError, setCfError] = useState(null);
  const [dnsVerified, setDnsVerified] = useState(false);
  const [verifyingDns, setVerifyingDns] = useState(false);
  const [openGuide, setOpenGuide] = useState(null);

  // Auth
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("register");
  const [showPassword, setShowPassword] = useState(false);
  const [authData, setAuthData] = useState({ email: "", password: "" });

  const tlds = [
    { tld: ".de", price: "9.99" }, { tld: ".com", price: "12.99" },
    { tld: ".net", price: "14.99" }, { tld: ".at", price: "14.99" }, { tld: ".ch", price: "19.99" }
  ];

  const debouncedDomain = useDebounce(customDomain, 500);

  useEffect(() => { fetchPage(); fetchServerInfo(); }, [pageId]);

  useEffect(() => {
    if (page && !customDomain) {
      const suggested = page.company_name.toLowerCase()
        .replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
      setCustomDomain(suggested);
    }
  }, [page]);

  useEffect(() => {
    if (debouncedDomain && debouncedDomain.length >= 3 && domainType === "custom") {
      checkDomainAvailability(debouncedDomain + selectedTld);
    } else {
      setDomainStatus(null); setSuggestions([]);
    }
  }, [debouncedDomain, selectedTld, domainType]);

  const fetchPage = async () => {
    try {
      const res = await axios.get(`${API}/pages/${pageId}`);
      setPage(res.data);
    } catch {
      toast.error("Seite nicht gefunden");
      navigate("/assistent");
    } finally {
      setLoading(false);
    }
  };

  const fetchServerInfo = async () => {
    try {
      const res = await axios.get(`${API}/domain/server-info`);
      setServerInfo(res.data);
    } catch {}
  };

  const checkDomainAvailability = async (domain) => {
    setCheckingDomain(true); setDomainStatus(null); setDomainError(""); setSuggestions([]); setDomainSource("");
    try {
      const res = await axios.post(`${API}/domain-check-with-context/${pageId}`, { domain });
      if (res.data.error) { setDomainStatus("error"); setDomainError(res.data.error); }
      else if (res.data.available) { setDomainStatus("available"); setDomainPrice({ price: res.data.price, currency: res.data.currency }); }
      else { setDomainStatus("taken"); setSuggestions(res.data.suggestions || []); }
      setDomainSource(res.data.source || "");
    } catch { setDomainStatus("error"); setDomainError("Fehler bei der Überprüfung"); }
    finally { setCheckingDomain(false); }
  };

  const detectProvider = async () => {
    if (!connectDomain.trim()) return;
    setDetectingProvider(true);
    // Step 1: detect current registrar
    try {
      const provRes = await axios.post(`${API}/domain/detect-provider`, { domain: connectDomain.trim() });
      setDetectedProvider(provRes.data.provider || null);
    } catch {}
    // Step 2: add domain to Cloudflare zone → get nameservers
    try {
      const cfRes = await axios.post(`${API}/domain/cloudflare-setup`, { domain: connectDomain.trim() });
      setNameservers(cfRes.data.nameservers || []);
      setCfError(null);
    } catch (e) {
      setNameservers([]);
      setCfError(e.response?.data?.detail || "Cloudflare nicht konfiguriert");
    }
    setDetectingProvider(false);
    setConnectStep("instructions");
    if (!serverInfo) fetchServerInfo();
  };

  const verifyDns = async () => {
    setVerifyingDns(true);
    try {
      const checkType = nameservers.length > 0 ? "ns" : "dns";
      const res = await axios.post(`${API}/domain/verify-dns`, { domain: connectDomain.trim(), check_type: checkType });
      if (res.data.verified) {
        setDnsVerified(true);
        toast.success(nameservers.length > 0 ? "Nameserver aktiv! Deine Seite wird jetzt konfiguriert." : "DNS-Einträge erkannt! Du kannst jetzt live gehen.");
      } else {
        const hint = nameservers.length > 0
          ? "Nameserver noch nicht aktiv. Das kann bis zu 48h dauern."
          : "DNS-Einträge noch nicht aktiv. Prüfung kann bis zu 24h dauern.";
        toast.error(hint);
      }
    } catch { toast.error("Prüfung fehlgeschlagen. Bitte erneut versuchen."); }
    setVerifyingDns(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("In Zwischenablage kopiert");
  };

  const handlePublish = async () => {
    if (!domainType) return toast.error("Bitte wähle eine Option");
    if (domainType === "custom" && domainStatus !== "available") return toast.error("Bitte wähle eine verfügbare Domain");
    if (domainType === "connect" && !connectDomain.trim()) return toast.error("Bitte gib deine Domain ein");

    const token = localStorage.getItem("auth_token");
    if (!token) { setShowAuth(true); return; }
    await publishPage(token);
  };

  const publishPage = async (token) => {
    setPublishing(true);
    const statuses = [
      { key: "check", text: "Domain wird geprüft..." },
      { key: "setup", text: domainType === "connect" ? "Domain wird verbunden..." : "Domain wird registriert..." },
      { key: "ssl", text: "SSL wird aktiviert..." },
      { key: "deploy", text: "Seite wird veröffentlicht..." }
    ];
    let idx = 0;
    setPublishingStatus(statuses[0].key);
    const iv = setInterval(() => {
      idx = Math.min(idx + 1, statuses.length - 1);
      setPublishingStatus(statuses[idx].key);
    }, 2000);

    try {
      await axios.post(
        `${API}/pages/${pageId}/publish-domain`,
        {
          domain_type: domainType,
          subdomain: null,
          custom_domain: domainType === "custom"
            ? customDomain + selectedTld
            : domainType === "connect"
            ? connectDomain.trim()
            : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      clearInterval(iv);
      toast.success("Seite erfolgreich veröffentlicht!");
      navigate(`/success/${pageId}`);
    } catch (err) {
      clearInterval(iv);
      toast.error(err.response?.data?.detail || "Fehler beim Veröffentlichen");
      setPublishing(false);
      setPublishingStatus("");
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!authData.email || !authData.password) return toast.error("Bitte fülle alle Felder aus");
    setPublishing(true);
    try {
      const endpoint = authMode === "register" ? "/auth/register" : "/auth/login";
      const payload = authMode === "register" ? { ...authData, page_id: pageId } : authData;
      const res = await axios.post(`${API}${endpoint}`, payload);
      localStorage.setItem("auth_token", res.data.token);
      toast.success(authMode === "register" ? "Account erstellt!" : "Erfolgreich angemeldet!");
      setShowAuth(false);
      await publishPage(res.data.token);
    } catch (err) {
      if (err.response?.data?.detail?.includes("bereits registriert")) {
        toast.error("E-Mail bereits registriert. Bitte anmelden.");
        setAuthMode("login");
      } else {
        toast.error("Fehler bei der Authentifizierung");
      }
      setPublishing(false);
    }
  };

  // Publishing progress screen
  if (publishing && publishingStatus) {
    const statuses = [
      { key: "check", text: "Domain wird geprüft", icon: Search },
      { key: "setup", text: domainType === "connect" ? "Domain wird verbunden" : "Domain wird registriert", icon: Globe },
      { key: "ssl", text: "SSL wird aktiviert", icon: Lock },
      { key: "deploy", text: "Seite wird veröffentlicht", icon: Zap }
    ];
    const currentIdx = statuses.findIndex(s => s.key === publishingStatus);
    const displayDomain = domainType === "custom" ? customDomain + selectedTld : connectDomain;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-8 relative">
            <div className="absolute inset-0 bg-indigo-100 rounded-full animate-ping opacity-25" />
            <div className="relative w-full h-full bg-indigo-600 rounded-full flex items-center justify-center">
              <Globe className="w-10 h-10 text-white animate-pulse" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Seite wird live geschaltet</h2>
          <p className="text-slate-500 mb-8">{displayDomain}</p>
          <div className="space-y-3 mb-8">
            {statuses.map((s, i) => {
              const Icon = s.icon;
              const isActive = s.key === publishingStatus;
              const isPast = currentIdx > i;
              return (
                <div key={s.key} className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
                  isActive ? "bg-indigo-50 text-indigo-700" : isPast ? "text-green-600" : "text-slate-400"
                }`}>
                  {isPast ? <CheckCircle2 className="w-6 h-6" /> : isActive ? <Loader2 className="w-6 h-6 animate-spin" /> : <Icon className="w-6 h-6" />}
                  <span className="font-medium">{s.text}</span>
                </div>
              );
            })}
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all duration-500"
              style={{ width: `${((currentIdx + 1) / statuses.length) * 100}%` }} />
          </div>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
    </div>
  );

  const serverTarget = serverInfo?.record_value || "";
  const recordType = serverInfo?.record_type || "CNAME";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 px-4 md:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate(`/vorschau/${pageId}`)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Zurück zur Vorschau</span>
          </button>
          <Logo size="md" clickable={false} />
          <div className="w-20" />
        </div>
      </header>

      <main className="px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 mx-auto mb-6 bg-indigo-100 rounded-2xl flex items-center justify-center">
              <Globe className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
              Wie möchtest du deine Webseite veröffentlichen?
            </h1>
            <p className="text-slate-500 text-sm">Wähle die passende Option für dein Business</p>
          </div>

          {/* ── OPTION A: New domain (Empfohlen) ── */}
          <div
            data-testid="domain-option-custom"
            onClick={() => { setDomainType("custom"); setConnectStep("input"); }}
            className={`bg-white rounded-2xl p-6 mb-4 border-2 cursor-pointer transition-all hover:shadow-lg ${
              domainType === "custom" ? "border-indigo-600 shadow-lg" : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                domainType === "custom" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"
              }`}>
                <Crown className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-slate-900">Eine Wunschdomain</h3>
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">Empfohlen</span>
                </div>
                <p className="text-slate-500 text-sm">Neue, professionelle Domain für dein Business registrieren</p>

                {domainType === "custom" && (
                  <div className="mt-4 space-y-4">
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <Label className="text-sm text-slate-600 mb-2 block">Domain suchen:</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input value={customDomain} onChange={e => setCustomDomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                            className="h-12 pr-10 font-mono text-base" placeholder="mueller-fensterreinigung" />
                          {checkingDomain && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-600 animate-spin" />}
                          {!checkingDomain && domainStatus === "available" && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />}
                          {!checkingDomain && domainStatus === "taken" && <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />}
                          {!checkingDomain && domainStatus === "error" && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500" />}
                        </div>
                        <select value={selectedTld} onChange={e => setSelectedTld(e.target.value)}
                          className="h-12 px-3 rounded-lg border border-slate-200 bg-white font-medium text-slate-700">
                          {tlds.map(t => <option key={t.tld} value={t.tld}>{t.tld}</option>)}
                        </select>
                      </div>
                      {domainStatus === "available" && domainPrice && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="font-medium">{customDomain}{selectedTld} ist verfügbar!</span>
                          </div>
                          <span className="font-bold text-green-700">{domainPrice.price} €/Jahr</span>
                        </div>
                      )}
                      {domainStatus === "taken" && (
                        <div className="mt-3 p-3 bg-red-50 rounded-lg flex items-center gap-2 text-red-700">
                          <XCircle className="w-5 h-5" />
                          <span className="font-medium">{customDomain}{selectedTld} ist bereits vergeben</span>
                        </div>
                      )}
                      {domainStatus === "error" && domainError && (
                        <div className="mt-3 p-3 bg-amber-50 rounded-lg flex items-center gap-2 text-amber-700">
                          <AlertCircle className="w-5 h-5 shrink-0" /><span>{domainError}</span>
                        </div>
                      )}
                      {checkingDomain && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg flex items-center gap-2 text-blue-700">
                          <Loader2 className="w-5 h-5 animate-spin" /><span>Wird geprüft...</span>
                        </div>
                      )}
                    </div>
                    {suggestions.length > 0 && (
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <Label className="text-sm text-slate-600 mb-3 flex items-center gap-2">
                          <Sparkles className="w-4 h-4" /> Verfügbare Alternativen:
                        </Label>
                        <div className="space-y-2">
                          {suggestions.map((s, i) => (
                            <button key={i} onClick={e => { e.stopPropagation(); const p = s.domain.split('.'); setCustomDomain(p.slice(0,-1).join('.')); const t = '.'+p[p.length-1]; if (tlds.find(x=>x.tld===t)) setSelectedTld(t); }}
                              className="w-full p-3 bg-white rounded-lg border border-slate-200 flex items-center justify-between hover:border-indigo-300 hover:bg-indigo-50 transition-all">
                              <span className="font-mono text-slate-700">{s.domain}</span>
                              <span className="text-sm text-slate-500">{s.price} €/Jahr</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-sm text-slate-600"><Shield className="w-4 h-4 text-green-500" /> SSL inklusive</div>
                      <div className="flex items-center gap-2 text-sm text-slate-600"><Zap className="w-4 h-4 text-amber-500" /> Meist noch am selben Tag live</div>
                    </div>
                  </div>
                )}
              </div>
              {domainType === "custom" && <CheckCircle2 className="w-6 h-6 text-indigo-600 shrink-0" />}
            </div>
          </div>

          {/* ── OPTION B: Connect existing domain ── */}
          <div
            data-testid="domain-option-connect"
            onClick={() => setDomainType("connect")}
            className={`bg-white rounded-2xl p-6 mb-6 border-2 cursor-pointer transition-all hover:shadow-lg ${
              domainType === "connect" ? "border-indigo-600 shadow-lg" : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                domainType === "connect" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"
              }`}>
                <Link2 className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-slate-900">Bestehende Domain verbinden</h3>
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">Ich habe bereits eine Domain</span>
                </div>
                <p className="text-slate-500 text-sm">Domain bleibt bei deinem Anbieter – nur DNS verbinden</p>

                {domainType === "connect" && (
                  <div className="mt-4 space-y-4" onClick={e => e.stopPropagation()}>

                    {/* Step 1: Enter domain */}
                    {connectStep === "input" && (
                      <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                        <Label className="text-sm text-slate-600 block">Deine bestehende Domain:</Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                              data-testid="connect-domain-input"
                              type="text"
                              value={connectDomain}
                              onChange={e => setConnectDomain(e.target.value.trim())}
                              placeholder="example.de"
                              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm bg-white"
                              onKeyDown={e => e.key === "Enter" && connectDomain && detectProvider()}
                            />
                          </div>
                          <Button
                            data-testid="connect-domain-next"
                            onClick={detectProvider}
                            disabled={!connectDomain.trim() || detectingProvider}
                            className="h-11 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
                          >
                            {detectingProvider ? <Loader2 className="w-4 h-4 animate-spin" /> : "Weiter"}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          Deine bestehende Webseite bleibt online, bis du umstellst.
                        </p>
                      </div>
                    )}

                    {/* Step 2: DNS instructions */}
                    {connectStep === "instructions" && (
                      <div className="space-y-4">
                        {/* Provider badge */}
                        {detectedProvider && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                            <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                            <p className="text-sm text-indigo-800">
                              <strong>Erkannt:</strong> Deine Domain liegt bei <strong>{detectedProvider}</strong>.
                            </p>
                          </div>
                        )}

                        {/* Reassurance */}
                        <div className="flex items-start gap-2 px-3 py-2 bg-green-50 border border-green-100 rounded-lg">
                          <Shield className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                          <p className="text-sm text-green-800">Deine bestehende Webseite bleibt online, bis du umstellst.</p>
                        </div>

                        {/* Cloudflare Nameserver path (preferred) */}
                        {nameservers.length > 0 ? (
                          <>
                            {/* CF badge */}
                            <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                              <Globe className="w-4 h-4 text-orange-500 shrink-0" />
                              <p className="text-sm text-orange-800">
                                <strong>Deine Domain wurde zu Cloudflare hinzugefügt.</strong>{" "}
                                Tausche jetzt die Nameserver bei deinem Anbieter aus.
                              </p>
                            </div>

                            {/* NS Records */}
                            <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm" data-testid="dns-records">
                              <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">Nameserver bei deinem Anbieter setzen:</p>
                              <div className="space-y-2">
                                {nameservers.map((ns, i) => (
                                  <div key={ns} className="flex items-center justify-between gap-2 bg-slate-800 rounded-lg px-3 py-2">
                                    <div>
                                      <span className="text-amber-400 font-bold">NS{i + 1}</span>
                                      <span className="text-slate-400 mx-2">→</span>
                                      <span className="text-green-400">{ns}</span>
                                    </div>
                                    <button onClick={() => copyToClipboard(ns)} className="text-slate-500 hover:text-white transition-colors">
                                      <Copy className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <p className="text-slate-500 text-xs mt-3">Nameserver-Änderungen können bis zu 48h dauern</p>
                            </div>

                            {/* Provider guides – Nameserver ändern */}
                            <div>
                              <p className="text-sm font-semibold text-slate-700 mb-2">Schritt-für-Schritt Anleitung:</p>
                              <div className="space-y-2">
                                {Object.keys(PROVIDER_GUIDES).map(p => (
                                  <div key={p} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                                    <button
                                      onClick={() => setOpenGuide(openGuide === p ? null : p)}
                                      className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-800 hover:bg-slate-50 transition-colors"
                                    >
                                      <span>{p}</span>
                                      {openGuide === p ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                    </button>
                                    {openGuide === p && (
                                      <div className="px-4 pb-4 bg-slate-50 border-t border-slate-100">
                                        <DNSGuide provider={p} ns1={nameservers[0] || "NS1"} ns2={nameservers[1] || "NS2"} />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Benefit box */}
                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-3">
                              <Zap className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                              <div className="text-xs text-blue-800">
                                <strong>Warum Cloudflare?</strong> Kostenloser CDN, automatisches SSL, CNAME-Flattening für Root-Domains und schnellere Ladezeiten.
                              </div>
                            </div>
                          </>
                        ) : (
                          /* Fallback: manual CNAME if CF not configured */
                          <>
                            {cfError && (
                              <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-800">Cloudflare automatisch nicht verfügbar. Bitte nutze die manuelle DNS-Methode:</p>
                              </div>
                            )}
                            <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm" data-testid="dns-records">
                              <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">DNS-Einträge bei deinem Anbieter setzen:</p>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2 bg-slate-800 rounded-lg px-3 py-2">
                                  <div>
                                    <span className="text-amber-400 font-bold">{recordType}</span>
                                    <span className="text-slate-400 mx-2">@</span>
                                    <span className="text-green-400">{serverTarget || "—"}</span>
                                  </div>
                                  {serverTarget && <button onClick={() => copyToClipboard(serverTarget)} className="text-slate-500 hover:text-white transition-colors"><Copy className="w-4 h-4" /></button>}
                                </div>
                              </div>
                              <p className="text-slate-500 text-xs mt-3">TTL: 3600 | Änderungen können bis zu 24h dauern</p>
                            </div>
                          </>
                        )}

                        {/* Verify button */}
                        <div className="flex items-center gap-3">
                          <Button
                            data-testid="verify-dns-btn"
                            onClick={verifyDns}
                            disabled={verifyingDns}
                            variant="outline"
                            className="flex-1 h-10 rounded-xl border-indigo-200 text-indigo-700 hover:bg-indigo-50 text-sm font-medium"
                          >
                            {verifyingDns ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Prüfe…</> : <><Search className="w-4 h-4 mr-2" />{nameservers.length > 0 ? "Nameserver prüfen" : "DNS-Einträge prüfen"}</>}
                          </Button>
                          {dnsVerified && <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" data-testid="dns-verified-icon" />}
                        </div>

                        {/* Upsell */}
                        <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-100 rounded-xl p-4" data-testid="dns-setup-upsell">
                          <div className="flex items-start gap-3">
                            <Sparkles className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-semibold text-slate-900 mb-0.5">Wir übernehmen die Einrichtung für dich</p>
                              <p className="text-xs text-slate-500">Einmalig 29€ – oder im Pro-Paket inklusive. Kein technisches Wissen nötig.</p>
                            </div>
                          </div>
                        </div>

                        <button onClick={() => { setConnectStep("input"); setDetectedProvider(null); setNameservers([]); setCfError(null); }} className="text-xs text-slate-400 hover:text-slate-600 underline">
                          Domain ändern
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {domainType === "connect" && <CheckCircle2 className="w-6 h-6 text-indigo-600 shrink-0" />}
            </div>
          </div>

          {/* Publish Button */}
          <Button
            data-testid="publish-btn"
            onClick={handlePublish}
            disabled={
              !domainType ||
              (domainType === "custom" && domainStatus !== "available") ||
              (domainType === "connect" && connectStep === "input")
            }
            className="w-full h-14 rounded-full bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 hover:from-indigo-500 hover:via-blue-500 hover:to-violet-500 font-semibold text-lg text-white shadow-lg shadow-indigo-500/25 transition-all"
          >
            {domainType === "connect" ? (
              <><Globe className="w-5 h-5 mr-2" />Domain verbinden & Seite live schalten</>
            ) : domainType === "custom" ? (
              <><Crown className="w-5 h-5 mr-2" />Domain sichern & Seite live schalten</>
            ) : (
              <><Globe className="w-5 h-5 mr-2" />Seite live schalten</>
            )}
          </Button>

          <div className="mt-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Lock className="w-4 h-4" />
            <span>SSL-Zertifikat wird automatisch aktiviert</span>
          </div>
        </div>
      </main>

      {/* Auth Modal */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-4 bg-indigo-100 rounded-2xl flex items-center justify-center">
              <Mail className="w-7 h-7 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              {authMode === "register" ? "Account erstellen" : "Anmelden"}
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              {authMode === "register" ? "Erstelle einen Account um deine Seite zu verwalten" : "Melde dich an um fortzufahren"}
            </p>
          </div>
          <form onSubmit={handleAuth} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700">E-Mail</Label>
              <Input type="email" value={authData.email} onChange={e => setAuthData(p => ({ ...p, email: e.target.value }))} placeholder="deine@email.de" className="h-12" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Passwort</Label>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} value={authData.password} onChange={e => setAuthData(p => ({ ...p, password: e.target.value }))} placeholder="••••••••" className="h-12 pr-12" />
                <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={publishing} className="w-full h-12 rounded-full bg-indigo-600 hover:bg-indigo-700">
              {publishing ? <Loader2 className="w-5 h-5 animate-spin" /> : authMode === "register" ? "Account erstellen & veröffentlichen" : "Anmelden & veröffentlichen"}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <button onClick={() => setAuthMode(m => m === "register" ? "login" : "register")} className="text-sm text-indigo-600 hover:text-indigo-700">
              {authMode === "register" ? "Bereits einen Account? Anmelden" : "Noch keinen Account? Registrieren"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
